/**
 * Route guard (platform-design §1 / api.md §3, adopted by DESIGN2 §11):
 * the SPA probes `GET /api/auth/session` (deliberately never a 401) and
 * renders the protected tree only when authenticated. Unauthenticated →
 * redirect to /login carrying the attempted URL as `?next=` so login lands
 * the user back where they were headed.
 *
 * A refresh is not a new visit (2026-09-06; owner: "I don't like how
 * there's always that slow checking your session screen"): when the last
 * probe said signed in (rememberedAuth), the tree renders at once and the
 * probe runs behind it; a probe that comes back "not signed in" still sends
 * the reader to login. With nothing remembered, the wait is a blank, not a
 * sentence.
 */

import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';

import { useAuthSession } from '../../api/queries';
import { readRememberedAuth, rememberAuth } from './rememberedAuth';

/** Build the /login URL that returns to `pathname+search` after login. */
export function loginPathWithNext(pathname: string, search: string): string {
  const next = `${pathname}${search}`;
  if (next === '/' || next.length === 0) return '/login';
  return `/login?next=${encodeURIComponent(next)}`;
}

export function RequireAuth() {
  const probe = useAuthSession();
  const location = useLocation();
  // Read once, at mount: it decides what the FIRST paint shows, and the probe
  // (not a later render) is what updates the memory.
  const [remembered] = useState(readRememberedAuth);

  useEffect(() => {
    if (probe.data !== undefined) rememberAuth(probe.data.authenticated ? 'in' : 'out');
  }, [probe.data]);

  if (probe.isPending) {
    if (remembered === 'in') return <Outlet />;
    return <div className="h-full" data-testid="auth-probe-pending" />;
  }

  // A failed probe (server down) reads as unauthenticated — the login screen
  // is the one place that can render without the API.
  if (probe.isError || probe.data.authenticated !== true) {
    return <Navigate to={loginPathWithNext(location.pathname, location.search)} replace />;
  }

  return <Outlet />;
}
